import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './cssfile/dashboard.css';

import ExamHeader from './exam/ExamHeader';
import QuestionPanel from './exam/QuestionPanel';
import ExamActions from './exam/ExamActions';
import QuestionPalette from './exam/QuestionPalette';
import MobileQuestionModal from './exam/MobileQuestionModal';
import useExamTimer from './hooks/useExamTimer';
import {
  getSections,
  getDashboardCategory,
  getTimeBackup,
  getMarkedBackup,
  getSavedBackup,
  getVisitedBackup,
  getQuestions,
  saveQuestion,
  markQuestion,
  visitQuestion,
  deleteResponse,
  submitTest,
  saveTemporaryExam,
} from './services/examApi';

const getExamIdentity = () => ({
  examId: localStorage.getItem('exam_id'),
  regid: localStorage.getItem('regid'),
  email: localStorage.getItem('email'),
});

// Keep the selected section isolated per test series.
// The old global `secl_id` caused a section from a previous test
// to be restored when the user started another test.
const getSectionStorageKey = examId => (
  examId ? `secl_id_${String(examId)}` : 'secl_id'
);

const getStoredSectionId = examId => {
  if (!examId) return null;

  const examSpecificKey = getSectionStorageKey(examId);
  const stored = localStorage.getItem(examSpecificKey);

  // Do not migrate the old global `secl_id`: it belongs to an
  // unknown/possibly previous test series and is the source of the bug.
  return stored || null;
};

const setStoredSectionId = (examId, sectionId) => {
  if (!examId || !sectionId) return;

  const examSpecificKey = getSectionStorageKey(examId);
  localStorage.setItem(examSpecificKey, String(sectionId));

  // Keep the legacy key synchronized for any older code, but only
  // after we have explicitly selected a valid section for this exam.
  localStorage.setItem('secl_id', String(sectionId));
};

const parseTimeToMilliseconds = value => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(value, 0);
  if (typeof value !== 'string') return 0;

  const raw = value.trim();
  if (!raw) return 0;

  if (/^\d+(\.\d+)?$/.test(raw)) {
    const numericValue = Number(raw);
    return Number.isFinite(numericValue) ? Math.max(numericValue, 0) : 0;
  }

  if (!raw.includes(':')) return 0;

  const parts = raw.split(':').map(Number);
  if (parts.some(Number.isNaN) || parts.length < 2 || parts.length > 3) return 0;

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return ((hours * 3600) + (minutes * 60) + seconds) * 1000;
  }

  const [minutes, seconds] = parts;
  return ((minutes * 60) + seconds) * 1000;
};

const getSavedAnswer = (question, savedItems) => {
  const saved = (savedItems || []).find(
    item => String(item.q_id) === String(question?.q_id)
  );

  if (!saved) return '';

  const answer = String(saved.answer || '').toUpperCase();
  if (['A', 'B', 'C', 'D'].includes(answer)) return answer;

  const legacyOptions = [question.option1, question.option2, question.option3, question.option4];
  const legacyIndex = legacyOptions.findIndex(option => option === saved.answer);
  return legacyIndex >= 0 ? String.fromCharCode(65 + legacyIndex) : '';
};

const uniqueCount = items => new Set((items || []).map(item => String(item.q_id))).size;

const Edashboard = props => {
  const { history } = props;
  const { examId, regid, email } = useMemo(getExamIdentity, []);

  const [showNavigator, setShowNavigator] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [sections, setSections] = useState([]);
  const [dcategory, setDcategory] = useState([]);
  const [timeBack, setTimeBack] = useState([]);
  const [markedbackup, setMarkedbackup] = useState([]);
  const [savedbackup, setSavedbackup] = useState([]);
  const [visitedbackup, setVisitedbackup] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [timerMilliseconds, setTimerMilliseconds] = useState(null);
  const [backupReady, setBackupReady] = useState(false);
  const timeLeftRef = useRef(null);

  const currentQuestion = currentQuestionIndex !== null ? questions[currentQuestionIndex] : null;

  const loadBackups = useCallback(async () => {
    if (!examId || !regid) return;

    try {
      const [marked, saved, visited] = await Promise.all([
        getMarkedBackup(examId, regid),
        getSavedBackup(examId, regid),
        getVisitedBackup(examId, regid),
      ]);

      setMarkedbackup(Array.isArray(marked.data) ? marked.data : []);
      setSavedbackup(Array.isArray(saved.data) ? saved.data : []);
      setVisitedbackup(Array.isArray(visited.data) ? visited.data : []);

      return {
        marked: Array.isArray(marked.data) ? marked.data : [],
        saved: Array.isArray(saved.data) ? saved.data : [],
        visited: Array.isArray(visited.data) ? visited.data : [],
      };
    } catch (error) {
      console.error('Unable to load question backups:', error);
      return { marked: [], saved: [], visited: [] };
    }
  }, [examId, regid]);

  const loadQuestion = useCallback(async index => {
    if (!examId) return;

    const sectionId = getStoredSectionId(examId);
    if (!sectionId) return;

    try {
      const response = await getQuestions(sectionId, index);
      const loadedQuestions = Array.isArray(response.data) ? response.data : [];
      const nextIndex = index === undefined || index === null ? 0 : index;
      const question = loadedQuestions[nextIndex] || loadedQuestions[0] || null;
      const backups = await loadBackups();

      setQuestions(loadedQuestions);
      setCurrentQuestionIndex(question ? nextIndex : null);
      setSelectedAnswer(question ? getSavedAnswer(question, backups.saved) : '');
    } catch (error) {
      console.error('Unable to load question:', error);
    }
  }, [examId, loadBackups]);

  const loadAllQuestions = useCallback(async (sectionId, sectionName) => {
    if (!sectionId) return;

    setStoredSectionId(examId, sectionId);

    try {
      const response = await getQuestions(sectionId);
      const loadedQuestions = Array.isArray(response.data) ? response.data : [];
      const currentSectionIndex = sections.findIndex(section => String(section.sec_id) === String(sectionId));
      const nextSection = sections[currentSectionIndex + 1];

      if (nextSection && loadedQuestions.length === 0) {
        await loadAllQuestions(nextSection.sec_id, nextSection.section_name);
        return;
      }

      const backups = await loadBackups();
      const firstQuestion = loadedQuestions[0] || null;

      setQuestions(loadedQuestions);
      setCurrentSection(sectionName || sections[currentSectionIndex]?.section_name || null);
      setCurrentQuestionIndex(firstQuestion ? 0 : null);
      setSelectedAnswer(firstQuestion ? getSavedAnswer(firstQuestion, backups.saved) : '');
    } catch (error) {
      console.error('Unable to load section questions:', error);
    }
  }, [sections, loadBackups]);

  useEffect(() => {
    if (!email || !regid) {
      history.push('/login');
      return undefined;
    }

    let cancelled = false;

    const initializeExam = async () => {
      try {
        const [sectionsResponse, categoryResponse, timeResponse] = await Promise.all([
          getSections(examId),
          getDashboardCategory(examId),
          getTimeBackup(examId, regid),
        ]);

        if (cancelled) return;

        const loadedSections = Array.isArray(sectionsResponse.data) ? sectionsResponse.data : [];
        const loadedCategory = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
        const loadedTimeBackup = Array.isArray(timeResponse.data) ? timeResponse.data : [];

        setSections(loadedSections);
        setDcategory(loadedCategory);
        setTimeBack(loadedTimeBackup);

        const backupTime = loadedTimeBackup[0]?.last_time;
        const initialTime = backupTime || loadedCategory[0]?.time;
        const milliseconds = parseTimeToMilliseconds(initialTime);
        if (milliseconds > 0) setTimerMilliseconds(milliseconds);

        setBackupReady(true);

        // Restore only a section that belongs to THIS exam/test series.
        // If no exam-specific section exists, always start from the first
        // section returned by the current exam. This prevents a previous
        // test's section/question list (e.g. 60 questions) from appearing.
        const storedSectionId = getStoredSectionId(examId);
        const savedSection = loadedSections.find(
          section => String(section.sec_id) === String(storedSectionId)
        );

        const initialSection = savedSection || loadedSections[0] || null;

        if (!savedSection && storedSectionId) {
          // The saved section does not belong to the current test.
          localStorage.removeItem(getSectionStorageKey(examId));
        }

        if (initialSection) {
          setStoredSectionId(examId, initialSection.sec_id);

          const questionResponse = await getQuestions(initialSection.sec_id);
          const loadedQuestions = Array.isArray(questionResponse.data) ? questionResponse.data : [];
          const backups = await loadBackups();

          if (!cancelled) {
            const firstQuestion = loadedQuestions[0] || null;
            setQuestions(loadedQuestions);
            setCurrentSection(initialSection.section_name || null);
            setCurrentQuestionIndex(firstQuestion ? 0 : null);
            setSelectedAnswer(firstQuestion ? getSavedAnswer(firstQuestion, backups.saved) : '');
          }
        } else if (!cancelled) {
          setQuestions([]);
          setCurrentSection(null);
          setCurrentQuestionIndex(null);
          setSelectedAnswer('');
        }
      } catch (error) {
        console.error('Unable to initialize exam:', error);
      }
    };

    initializeExam();
    return () => { cancelled = true; };
  }, [email, regid, examId, history, loadBackups]);

  const submitExam = useCallback(async autoSubmit => {
    if (!autoSubmit && !window.confirm('Are you sure you want to submit the test?')) return;

    const data = {
      email,
      regid,
      exam_id: examId,
      a_id: dcategory[0]?.a_id,
      time_taken: parseTimeToMilliseconds(timeLeftRef.current),
    };

    try {
      await submitTest(data);
      history.push('/result', { data });
    } catch (error) {
      console.error('Unable to submit test:', error);
    }
  }, [dcategory, email, regid, examId, history]);

  const timeLeft = useExamTimer(timerMilliseconds, () => submitExam(true));

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    if (!backupReady || !examId || !regid || !timeLeft) return undefined;

    const backupInterval = setInterval(async () => {
      const milliseconds = parseTimeToMilliseconds(timeLeftRef.current);
      if (!milliseconds) return;

      try {
        await saveTemporaryExam({
          email,
          regid,
          exam_id: examId,
          last_time: milliseconds,
          topic_name: dcategory[0]?.topic_name || timeBack[0]?.topic_name,
        });
      } catch (error) {
        console.error('Unable to save exam time backup:', error);
      }
    }, 40000);

    return () => clearInterval(backupInterval);
  }, [backupReady, examId, regid, email, dcategory, timeBack]);

  const handleFullScreenToggle = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullScreen(false);
      } else {
        await document.documentElement.requestFullscreen();
        setIsFullScreen(true);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  const getCurrentQuestionData = useCallback(() => ({
    email,
    regid,
    exam_id: examId,
    a_id: dcategory[0]?.a_id,
    q_id: currentQuestion?.q_id,
    answer: selectedAnswer || 0,
  }), [email, regid, examId, dcategory, currentQuestion, selectedAnswer]);

  const goToNextQuestion = useCallback(async () => {
    const nextIndex = (currentQuestionIndex || 0) + 1;

    if (nextIndex < questions.length) {
      await loadQuestion(nextIndex);
      return;
    }

    const currentSectionIndex = sections.findIndex(
      section => String(section.sec_id) === String(getStoredSectionId(examId))
    );
    const nextSection = sections[currentSectionIndex + 1];

    if (nextSection) {
      await loadAllQuestions(nextSection.sec_id, nextSection.section_name);
      return;
    }

    if (sections[0]) {
      await loadAllQuestions(sections[0].sec_id, sections[0].section_name);
    }
  }, [currentQuestionIndex, questions.length, sections, loadQuestion, loadAllQuestions]);

  const handleNextClick = async () => {
    if (!currentQuestion) return;

    try {
      const data = getCurrentQuestionData();
      await Promise.all([saveQuestion(data), visitQuestion(data)]);
      await loadBackups();
      await goToNextQuestion();
    } catch (error) {
      console.error('Unable to save answer:', error);
    }
  };

  const handleMarkQuestion = async () => {
    if (!currentQuestion) return;

    try {
      const data = getCurrentQuestionData();
      await Promise.all([markQuestion(data), visitQuestion(data)]);
      await loadBackups();
      await goToNextQuestion();
    } catch (error) {
      console.error('Unable to mark question:', error);
    }
  };

  const handleDeleteResponse = async qId => {
    try {
      await deleteResponse(examId, regid, qId);
      await loadQuestion(currentQuestionIndex);
    } catch (error) {
      console.error('Unable to clear response:', error);
    }
  };

  const handleQuestionSelect = async index => {
    await loadQuestion(index);
    setShowNavigator(false);
  };

  const handleSectionSelect = async (sectionId, sectionName) => {
    await loadAllQuestions(sectionId, sectionName);
  };

  const answeredCount = uniqueCount(savedbackup);
  const markedCount = uniqueCount(markedbackup);
  const visitedCount = uniqueCount(visitedbackup);
  const notVisitedCount = Math.max(0, questions.length - visitedCount);
  const notAnsweredCount = Math.max(0, questions.length - answeredCount);

  if (!email || !regid) return null;

  return (
    <div
      className="exam-shell"
      onCopy={event => {
        event.preventDefault();
        alert('Copying text is not allowed on this page.');
      }}
    >
      <ExamHeader
        categories={dcategory}
        timeLeft={timeLeft}
        isFullScreen={isFullScreen}
        onFullScreenToggle={handleFullScreenToggle}
        onShow={() => setShowNavigator(true)}
      />

      <main className="exam-layout">
        <section className="exam-main">
          <div className="section-toolbar">
            <div className="section-toolbar__label">Sections</div>
            <div className="section-tabs">
              {sections.map((item, index) => (
                <button
                  type="button"
                  key={item.sec_id || index}
                  className={`section-tab ${String(item.sec_id) === String(getStoredSectionId(examId)) ? 'section-tab--active' : ''}`}
                  onClick={() => handleSectionSelect(item.sec_id, item.section_name)}
                >
                  {item.section_name}
                </button>
              ))}
            </div>
            <div className="language-control">
              <span>Language</span>
              <select disabled defaultValue="">
                <option value="">English</option>
                <option value="hi">Hindi</option>
              </select>
            </div>
          </div>

          <div className="question-scroll-area">
            <QuestionPanel
              question={currentQuestion}
              currentQuestionIndex={currentQuestionIndex || 0}
              savedbackup={savedbackup}
              markedbackup={markedbackup}
              selectedAnswer={selectedAnswer}
              onAnswerChange={event => setSelectedAnswer(event.target.value)}
            />
          </div>

          {currentQuestion && (
            <ExamActions
              onMark={handleMarkQuestion}
              onClear={() => handleDeleteResponse(currentQuestion.q_id)}
              onNext={handleNextClick}
              onSubmit={() => submitExam(false)}
            />
          )}
        </section>

        <aside className="exam-sidebar">
          <div className="candidate-card">
            <div className="candidate-avatar">{String(email).charAt(0).toUpperCase()}</div>
            <div className="candidate-info">
              <span>Candidate</span>
              <strong title={email}>{email}</strong>
            </div>
          </div>

          <div className="progress-grid">
            <div className="progress-stat progress-stat--answered"><strong>{answeredCount}</strong><span>Answered</span></div>
            <div className="progress-stat progress-stat--marked"><strong>{markedCount}</strong><span>Marked</span></div>
            <div className="progress-stat progress-stat--unvisited"><strong>{notVisitedCount}</strong><span>Not visited</span></div>
            <div className="progress-stat progress-stat--unanswered"><strong>{notAnsweredCount}</strong><span>Not answered</span></div>
          </div>

          <div className="sidebar-section-title">
            <div>
              <span>Current section</span>
              <strong>{currentSection || 'Not selected'}</strong>
            </div>
            <span className="question-total">{questions.length} Qs</span>
          </div>

          <div className="palette-card">
            <QuestionPalette
              questions={questions}
              markedbackup={markedbackup}
              savedbackup={savedbackup}
              visitedbackup={visitedbackup}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionSelect={handleQuestionSelect}
            />
          </div>

          <div className="palette-legend">
            <span><i className="legend-dot legend-dot--answered" /> Answered</span>
            <span><i className="legend-dot legend-dot--marked" /> Marked</span>
            <span><i className="legend-dot legend-dot--visited" /> Visited</span>
            <span><i className="legend-dot legend-dot--unvisited" /> Not visited</span>
          </div>

          <button type="button" className="sidebar-submit" onClick={() => submitExam(false)}>
            Submit Test
          </button>
        </aside>
      </main>

      <MobileQuestionModal
        show={showNavigator}
        email={email}
        questions={questions}
        markedbackup={markedbackup}
        savedbackup={savedbackup}
        visitedbackup={visitedbackup}
        currentQuestionIndex={currentQuestionIndex}
        onHide={() => setShowNavigator(false)}
        onQuestionSelect={handleQuestionSelect}
        onSubmit={() => submitExam(false)}
      />
    </div>
  );
};

export default Edashboard;
